/**
 * §4 프레임 조립 — 16k mono PCM16, 320ms 고정 프레임 + uint32 LE seq 헤더.
 *
 *   [ seq:uint32 LE ][ pcm16 samples … ]   = 4 + 10,240 = 10,244 B
 *
 * 서버는 seq 갭으로 패킷 유실을 감지·계수한다(agy P2). 따라서 클라이언트가
 * 프레임을 드롭할 때 seq 를 **건너뛰어야** 한다 — seq 를 촘촘히 재발급하면
 * 유실이 서버에서 보이지 않게 되어 "조용한 유실 금지"(§1 원칙 3)를 어긴다.
 */

import { TARGET_RATE } from './resampler'

/** §4 고정 프레임 길이(ms) — 서버 `audio.FRAME_MS` 와 같은 값이어야 한다 */
export const FRAME_MS = 320
/** 320ms @ 16kHz = 5,120 샘플 */
export const FRAME_SAMPLES = (TARGET_RATE * FRAME_MS) / 1000
/** 5,120 * 2B = 10,240 B */
export const FRAME_BYTES = FRAME_SAMPLES * 2
export const SEQ_HEADER_BYTES = 4
export const FRAME_TOTAL_BYTES = SEQ_HEADER_BYTES + FRAME_BYTES

/** §4 프레임 상한 — 초과는 스펙 위반(서버가 연결 종료) */
export const MAX_FRAME_BYTES = 64 * 1024

/**
 * OTF1(FLAC 압축) 전송 계약 — §1 단일 소스. 값 변경 시 server/audio.py,
 * server/flac.py 와 반드시 같이 맞춘다(tests/test_inventory.py 가 문자열/값
 * 일치를 강제한다).
 *
 *   [0:4)  magic 'OTF1'   [4:8) seq uint32 LE   [8:9) flags u8   [9:11) nsamp u16 LE
 *   [11:)  payload = 컨테이너를 벗긴 bare native FLAC 프레임 바이트(0xFFF8 로 시작)
 */
export const OTF1_MAGIC = 'OTF1'
export const OTF1_HEADER_BYTES = 11
/** FRAME_SAMPLES 와 반드시 같아야 한다 — v1 에서 고정 */
export const OTF1_NSAMP = 5120
export const OTF1_FLAG_FLUSH = 0b0000_0001
export const LEGACY_FRAME_BYTES = 10244

export interface Frame {
  seq: number
  /** 전송 가능한 완성 프레임(헤더 포함) */
  buffer: ArrayBuffer
}

/**
 * PCM16 샘플 스트림을 고정 길이 프레임으로 자른다.
 * 잔여 샘플은 내부에 남아 다음 호출과 이어진다.
 */
export class Framer {
  private pending = new Int16Array(0)
  private nextSeq = 0

  /** 다음에 발급될 seq (테스트·진단용) */
  get seq(): number {
    return this.nextSeq
  }

  /** 아직 프레임이 되지 못한 샘플 수 */
  get pendingSamples(): number {
    return this.pending.length
  }

  push(samples: Int16Array): Frame[] {
    if (samples.length > 0) {
      const merged = new Int16Array(this.pending.length + samples.length)
      merged.set(this.pending, 0)
      merged.set(samples, this.pending.length)
      this.pending = merged
    }

    const frames: Frame[] = []
    let offset = 0
    while (this.pending.length - offset >= FRAME_SAMPLES) {
      const slice = this.pending.subarray(offset, offset + FRAME_SAMPLES)
      frames.push(this.pack(slice))
      offset += FRAME_SAMPLES
    }
    this.pending = this.pending.slice(offset)
    return frames
  }

  /**
   * 마지막 잔여 샘플을 무음으로 패딩해 내보낸다.
   * 뮤트·방향 전환·종료 시 진행 중 버퍼를 flush 하는 경로(§5 VAD 리셋 조건).
   */
  flush(): Frame | null {
    if (this.pending.length === 0) return null
    const padded = new Int16Array(FRAME_SAMPLES)
    padded.set(this.pending.subarray(0, Math.min(this.pending.length, FRAME_SAMPLES)))
    this.pending = new Int16Array(0)
    return this.pack(padded)
  }

  /**
   * 드롭한 프레임 수만큼 seq 를 전진시킨다 — 서버가 갭을 보게 한다(§4).
   * @returns 건너뛴 [from, to) seq 구간
   */
  skip(count: number): { from: number; to: number } {
    const from = this.nextSeq
    this.nextSeq = (this.nextSeq + count) >>> 0
    return { from, to: this.nextSeq }
  }

  reset(): void {
    this.pending = new Int16Array(0)
  }

  private pack(samples: Int16Array): Frame {
    const buffer = new ArrayBuffer(FRAME_TOTAL_BYTES)
    const view = new DataView(buffer)
    const seq = this.nextSeq
    view.setUint32(0, seq, true) // little-endian (§4)
    // Int16Array 를 바이트 오프셋 4 에 직접 얹는다 — 정렬 문제를 피하려 DataView 사용
    for (let i = 0; i < samples.length; i += 1) {
      view.setInt16(SEQ_HEADER_BYTES + i * 2, samples[i] as number, true)
    }
    this.nextSeq = (this.nextSeq + 1) >>> 0
    return { seq, buffer }
  }
}

/** 수신측(테스트·목 서버)에서 프레임을 되읽는다. */
export function unpackFrame(buffer: ArrayBuffer): { seq: number; samples: Int16Array } {
  if (buffer.byteLength < SEQ_HEADER_BYTES) throw new Error('프레임이 헤더보다 짧다')
  if (buffer.byteLength > MAX_FRAME_BYTES) throw new Error('프레임 상한 64KB 초과')
  const view = new DataView(buffer)
  const seq = view.getUint32(0, true)
  const count = (buffer.byteLength - SEQ_HEADER_BYTES) / 2
  const samples = new Int16Array(count)
  for (let i = 0; i < count; i += 1) {
    samples[i] = view.getInt16(SEQ_HEADER_BYTES + i * 2, true)
  }
  return { seq, samples }
}

/**
 * OTF1 압축 프레임을 wire 포맷으로 조립한다. PCM 인코딩·>=10244B 폴백 판단은
 * 원본 PCM 을 쥔 호출부(C2/C3) 책임 — 이 함수는 무조건 버퍼를 만들어 반환한다.
 */
export function packCompressed(
  seq: number,
  flacBytes: Uint8Array,
  nsamp: number,
  flags: number
): ArrayBuffer {
  if (nsamp !== OTF1_NSAMP) throw new Error(`nsamp 는 ${OTF1_NSAMP} 이어야 한다: ${nsamp}`)
  if ((flags & ~OTF1_FLAG_FLUSH) !== 0) throw new Error(`알 수 없는 flags 비트: ${flags}`)
  const buffer = new ArrayBuffer(OTF1_HEADER_BYTES + flacBytes.byteLength)
  const view = new DataView(buffer)
  view.setUint8(0, OTF1_MAGIC.charCodeAt(0))
  view.setUint8(1, OTF1_MAGIC.charCodeAt(1))
  view.setUint8(2, OTF1_MAGIC.charCodeAt(2))
  view.setUint8(3, OTF1_MAGIC.charCodeAt(3))
  view.setUint32(4, seq >>> 0, true) // little-endian (§4 관례와 통일)
  view.setUint8(8, flags)
  view.setUint16(9, nsamp, true)
  new Uint8Array(buffer, OTF1_HEADER_BYTES).set(flacBytes)
  return buffer
}

/** unpackAnyFrame 이 돌려주는 태그드 유니언 — A.3 판별 결과를 그대로 노출한다. */
export type AnyFrame =
  | { format: 'pcm'; seq: number; samples: Int16Array }
  | { format: 'otf1'; seq: number; flags: number; nsamp: number; payload: Uint8Array }

/**
 * A.3 판별 순서(양쪽 동일, 서버 server/audio.py 와 반드시 같은 순서):
 *   1. len==10244 AND OTF1 매직 → 모호 → 에러(절대 추측하지 않는다)
 *   2. len==10244 → 레거시 PCM
 *   3. OTF1 매직 → OTF1 압축
 *   4. 그 외 → 에러
 * 크기 상한(len>65536)이 판별보다 먼저다 — unpackFrame 과 동일하게 fail-closed.
 */
export function unpackAnyFrame(buffer: ArrayBuffer): AnyFrame {
  if (buffer.byteLength > MAX_FRAME_BYTES) throw new Error('프레임 상한 64KB 초과')
  const hasOtf1Magic =
    buffer.byteLength >= 4 &&
    new Uint8Array(buffer, 0, 4).every((b, i) => b === OTF1_MAGIC.charCodeAt(i))

  if (buffer.byteLength === LEGACY_FRAME_BYTES && hasOtf1Magic) {
    throw new Error('10244B 이면서 OTF1 매직 — 모호한 프레임(추측 금지)')
  }
  if (buffer.byteLength === LEGACY_FRAME_BYTES) {
    const { seq, samples } = unpackFrame(buffer)
    return { format: 'pcm', seq, samples }
  }
  if (hasOtf1Magic) {
    if (buffer.byteLength < OTF1_HEADER_BYTES) throw new Error('OTF1 프레임이 헤더보다 짧다')
    const view = new DataView(buffer)
    const seq = view.getUint32(4, true)
    const flags = view.getUint8(8)
    const nsamp = view.getUint16(9, true)
    const payload = new Uint8Array(buffer, OTF1_HEADER_BYTES)
    return { format: 'otf1', seq, flags, nsamp, payload }
  }
  throw new Error('알 수 없는 프레임 포맷')
}
